import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Clock, Navigation } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { getRestaurantInfo } from '../services/api';

const DEFAULT_INFO = {
  address: 'Jl. H. Moh. Bafadhal No.6, Cempaka Putih, Kec. Jelutung, Kota Jambi, Jambi 36134, Indonesia',
  locationCode: '9JV7+GQ Cempaka Putih, Jambi City, Jambi',
  phone: '+62 851-6998-6774',
  openHours: 'Buka sampai pukul 21.00',
  services: ['Dine-in', 'Takeaway', 'No-contact delivery', 'Order online']
};

const ContactSection = () => {
  const [info, setInfo] = useState(DEFAULT_INFO);

  useEffect(() => {
    const fetchData = async () => {
      const data = await getRestaurantInfo();
      if (data) {
        setInfo({ ...DEFAULT_INFO, ...data });
      }
    };
    fetchData();
  }, []);

  const services = Array.isArray(info.services) ? info.services : [];

  return (
    <section id="kontak" className="py-24 bg-gray-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm font-medium mb-4">
            Kunjungi Kami
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-4">
            Lokasi & Kontak
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Temukan kami dan nikmati kelezatan masakan Padang autentik
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.2851!2d103.6144354!3d-1.6062415!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e2588dd956a84ab%3A0x413b606d12c074a0!2sAroma%20Cempaka!5e0!3m2!1sid!2sid!4v1699000000000!5m2!1sid!2sid"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '400px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Lokasi Rumah Makan Aroma Cempaka"
            />
          </div>

          <div className="space-y-6">
            <Card className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors duration-300">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-2">Alamat Lengkap</h4>
                    <p className="text-gray-400 leading-relaxed">{info.address}</p>
                    <p className="text-gray-500 text-sm mt-2">Kode: {info.locationCode}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors duration-300">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-white mb-2">Telepon</h4>
                    <a
                      href={`tel:${info.phone}`}
                      className="text-amber-400 text-lg font-semibold hover:text-amber-300 transition-colors"
                    >
                      {info.phone}
                    </a>
                    <p className="text-gray-500 text-sm mt-1">Klik untuk menelepon</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors duration-300">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-2">Jam Operasional</h4>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-green-400 font-medium">{info.openHours}</span>
                    </div>
                    <p className="text-gray-500 text-sm mt-1">Buka setiap hari</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                asChild
                size="lg"
                className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(info.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Navigation className="w-5 h-5 mr-2" />
                  Petunjuk Arah
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10 transition-all duration-300"
              >
                <a href={`tel:${info.phone}`}>
                  <Phone className="w-5 h-5 mr-2" />
                  Hubungi Kami
                </a>
              </Button>
            </div>

            <div className="flex flex-wrap gap-3 pt-4">
              {services.map((service, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-gray-300 text-sm"
                >
                  {service}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
